import React, { useState, useEffect } from 'react';
import { Plus, X, Edit2, Trash2, Upload, Save } from 'lucide-react';
import { 
    getCarouselImages, 
    addCarouselImage, 
    updateCarouselImage, 
    deleteCarouselImage,
    uploadImageToCloudinary
} from '../../Firebase/Firebase';

const CarouselSection = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    image: null
  });

  // Load images on component mount
  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = () => {
    setLoading(true);
    try {
      getCarouselImages((images) => {
        setImages(images);
      });
    } catch (error) {
      console.error('Error loading carousel images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview('');
      setFormData(prev => ({ ...prev, image: null }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title.trim()) {
      console.log('Validation failed: Title is empty');
      return;
    }

    setLoading(true);
    try {
      let imageUrl = '';
      
      if (formData.image) {
        try {
          imageUrl = await uploadImageToCloudinary(formData.image);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          imageUrl = '';
        }
      } else if (editingId) {
        const existingImage = images.find(img => img.id === editingId);
        imageUrl = existingImage?.imageUrl || '';
      }

      const imageData = {
        title: formData.title.trim(),
        imageUrl
      };

      if (editingId) {
        await updateCarouselImage(editingId, imageData);
      } else {
        await addCarouselImage(imageData);
      }

      closeModal();
    } catch (error) {
      console.error('Error saving carousel image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (image) => {
    setEditingId(image.id);
    setFormData({
      title: image.title,
      image: null
    });
    setImagePreview(image.imageUrl);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carousel image?')) return;

    setLoading(true);
    try {
      await deleteCarouselImage(id);
    } catch (error) {
      console.error('Error deleting carousel image:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setFormData({ title: '', image: null });
    setImagePreview('');
    setEditingId(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({ title: '', image: null });
    setImagePreview('');
    setEditingId(null);
  };

  return (
    <>
      {/* Carousel Images Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Carousel Images</h2>
          <button
            onClick={openModal}
            className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Add Image</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
        
        {/* Carousel Images Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {loading && images.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">🖼️</div>
              <p className="text-gray-500 text-lg">No carousel images yet. Add your first image!</p>
            </div>
          ) : (
            images.map((image) => (
              <div
                key={image.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={image.imageUrl || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'}
                    alt={image.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
                    }}
                  />
                </div>
                <div className="p-3 sm:p-4 flex flex-col flex-1">
                  <h3 className="font-bold text-base sm:text-lg text-gray-800 mb-2 sm:mb-3">{image.title}</h3>
                  <div className="flex gap-1 sm:gap-2">
                    <button
                      onClick={() => handleEdit(image)}
                      className="flex-1 bg-blue-500 text-white px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center text-xs sm:text-sm min-w-0"
                    >
                      <Edit2 className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(image.id)}
                      className="flex-1 bg-red-500 text-white px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center text-xs sm:text-sm min-w-0"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Carousel Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-white/30 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {editingId ? 'Edit Carousel Image' : 'Add New Carousel Image'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Image Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 placeholder:font-medium text-gray-600"
                    placeholder="Enter image title"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Image</label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-orange-400 transition-colors text-gray-700">
                      <Upload className="w-5 h-5 mr-2" />
                      <span>Choose Image</span>
                      <input
                        type="file"
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-xl shadow-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const event = { target: { files: null } };
                            handleImageChange(event);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        {editingId ? <Save className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                        {editingId ? 'Update Image' : 'Add Image'}
                      </>
                    )}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CarouselSection; 